import "./style.scss";

import type { IChannel } from "containers/Channels/types";
import type { ChannelData, ChannelParams } from "containers/TermFormContainer";
import {
    isMilestoneThresholdAmountBased,
    resolveChannelMilestoneBaseColumn,
} from "containers/TermFormContainer/ChannelsStep/milestoneBaseColumnUtils";
import { formatNumber } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useTranslation } from "react-i18next";

interface IChannelsRecap {
    /**
     * Array with all channels chosen in this form.
     */
    chosenChannel: ChannelParams | undefined;
    /**
     * Complete data of the chosen channel (chosen channel only has id and milestones values).
     */
    fullChannelData: IChannel | undefined;
    /**
     * Boolean to know whether channel accepts milestones or not.
     */
    hasMilestones: boolean;
}

/**
 * Similar to the MilestonesInputs component except the information is hard data and cannot be modified.
 */
const ChannelsMilestonesRecap = ({ chosenChannel, fullChannelData, hasMilestones }: IChannelsRecap) => {
    const [t] = useTranslation();
    const { currencySymbol } = useCompanySettings();

    return (
        <>
            <div
                className="milestones-recap"
                id={`milestone-recap-${chosenChannel?.channelId}-${chosenChannel?.formatTypeId ?? "default"}`}
            >
                {chosenChannel &&
                    (chosenChannel.data as ChannelData[]).map((mile, index) => {
                        const previousIterationMilestone =
                            index > 0 ? +(chosenChannel.data as ChannelData[])[index - 1]?.quantity + 1 : 0;

                        const isLastLine = mile.quantity === Infinity || mile.quantity === "Infinity";
                        const mileQuantity = mile.quantity !== Infinity && mile?.quantity;
                        const milePercentage = mile.percentage === "" ? 0 : mile.percentage;

                        const milestoneBaseColumn = fullChannelData
                            ? resolveChannelMilestoneBaseColumn(fullChannelData, chosenChannel)
                            : chosenChannel.milestoneBaseColumn ?? "quantity";
                        const isAmountBasedThreshold = isMilestoneThresholdAmountBased(milestoneBaseColumn);
                        return (
                            <div
                                className="milestone-line"
                                key={`milestone-${index}`}
                                style={{
                                    marginBottom: `${
                                        hasMilestones && !isLastLine ? "1rem" : isLastLine ? "0.5rem" : ""
                                    }`,
                                    marginTop: `${index === 0 && "0.25rem"}`,
                                }}
                            >
                                <div className="left-side">
                                    <span id={`milestone-previous-${index}`}>
                                        {formatNumber(+previousIterationMilestone) ?? 0}
                                    </span>
                                    <span className="text-grey">
                                        {isLastLine &&
                                            (isAmountBasedThreshold ? currencySymbol : t("MilestonesForm.Sales"))}{" "}
                                        {t("MilestonesForm.To")}
                                    </span>
                                    {isLastLine ? (
                                        <span id={`milestone-current-last`}>{t("MilestonesForm.InfinityText")}</span>
                                    ) : (
                                        <span id={`milestone-current-${index}`}>
                                            {!hasMilestones
                                                ? t("MilestonesForm.InfinityText")
                                                : formatNumber(+mileQuantity) ?? 0}
                                        </span>
                                    )}
                                    {!isLastLine && (
                                        <span className="text-grey">
                                            {isAmountBasedThreshold ? currencySymbol : t("MilestonesForm.Sales")}
                                        </span>
                                    )}
                                </div>
                                <div className="right-side">
                                    <span className="percentage" id={`recap-milestone-${index}-percentage`}>
                                        {milePercentage ?? 0}
                                    </span>
                                    <span className="text-grey">{t("MilestonesForm.Percentage")}</span>
                                </div>
                            </div>
                        );
                    })}
            </div>
        </>
    );
};

export default ChannelsMilestonesRecap;
